// Empty on the web (same-origin '/api'); set to the hosted server's origin for the native app,
// whose page is served from capacitor://localhost.
const API_BASE_URL = (import.meta.env.VITE_API_BASE_URL ?? '').replace(/\/+$/, '')

export class ApiError extends Error {
  readonly status: number

  constructor(message: string, status: number) {
    super(message)
    this.name = 'ApiError'
    this.status = status
  }
}

type AuthHeaders = Record<string, string>

interface ApiClientConfig {
  /** Extra headers for every request, e.g. a bearer token read from secure storage. */
  getAuthHeaders?: () => AuthHeaders | Promise<AuthHeaders>
  /** Called when the server rejects the request as unauthenticated. */
  onUnauthorized?: () => void
}

let config: ApiClientConfig = {}

export function configureApiClient(next: ApiClientConfig): void {
  config = { ...config, ...next }
}

export function apiUrl(path: string): string {
  return `${API_BASE_URL}${path}`
}

/** Sends the request with auth headers; throws ApiError (and reports a 401) unless it succeeds. */
async function send(path: string, init?: RequestInit): Promise<Response> {
  const authHeaders = (await config.getAuthHeaders?.()) ?? {}
  const res = await fetch(apiUrl(path), {
    ...init,
    headers: { 'Content-Type': 'application/json', ...authHeaders, ...(init?.headers ?? {}) },
  })
  if (!res.ok) {
    if (res.status === 401) config.onUnauthorized?.()
    const errJson = (await res.json().catch(() => ({}))) as { error?: string }
    throw new ApiError(errJson.error ?? `Server error ${res.status}`, res.status)
  }
  return res
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await send(path, init)
  if (res.status === 204) return undefined as T
  return res.json() as Promise<T>
}

export const api = {
  get: <T>(path: string): Promise<T> => request<T>(path),
  post: <T>(path: string, body?: unknown): Promise<T> =>
    request<T>(path, { method: 'POST', body: body !== undefined ? JSON.stringify(body) : undefined }),
  patch: <T>(path: string, body?: unknown): Promise<T> =>
    request<T>(path, { method: 'PATCH', body: JSON.stringify(body) }),
  delete: <T>(path: string): Promise<T> => request<T>(path, { method: 'DELETE' }),
  /** POSTs and returns the raw response, for reading a streamed body. */
  stream: (path: string, body: unknown): Promise<Response> =>
    send(path, { method: 'POST', body: JSON.stringify(body) }),
}
